<script setup lang="ts">
  import { storeToRefs } from 'pinia'
  import { useDiwassCredentialsValidation } from '@/composables/validation/use-diwass-credentials-validation'
  import { useDiwassStore } from '@/store/diwass.store'
  import type { Customer } from '@/types/nest/customer'
  import {
    FormInput,
    UiButton,
    require,
    ConfirmationDialog,
    deepEqual,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, onMounted, onUnmounted, ref, toRaw } from 'vue'
  import { isAxiosError } from 'axios'
  import type { DiwassCredentials } from '@/types/nest/diwass'

  const { customer } = defineProps<{ customer: Customer }>()

  const diwassStore = useDiwassStore()
  const toastStore = useToastStore()

  const submitting = ref<boolean>(false)
  const deletingCredentials = ref<boolean>(false)

  const originalCredentials = ref<DiwassCredentials | null>()
  const modified = computed(() => !deepEqual(originalCredentials.value, diwassStore.credentials))

  const { validate, visibleErrors, submitted } = useDiwassCredentialsValidation(
    storeToRefs(diwassStore).credentials
  )

  async function submit() {
    if (!validate()) return

    if (diwassStore.credentials == null) throw Error('diwass credentials are null ')

    submitting.value = true
    try {
      const id = diwassStore.credentials.id

      if (id == null) {
        await diwassStore.createCredentials(diwassStore.credentials, customer.id)
      } else {
        await diwassStore.updateCredentials({ ...diwassStore.credentials, id })
      }
    } catch (error) {
      if (isAxiosError(error)) {
        toastStore.error('Er is iets fout gegaan!')
        submitting.value = false
        return
      }
      throw error
    }

    submitted.value = false
    submitting.value = false
    toastStore.success('Inloggegevens succesvol opgeslagen')

    await setup()
  }

  async function deleteCredentials() {
    await diwassStore.deleteCredentials(
      require(diwassStore.credentials, 'diwass credentials are null')
    )
    diwassStore.credentials = {
      id: undefined,
      username: '',
      webServiceClientId: '',
      authenticationKey: '',
    }
    originalCredentials.value = structuredClone(toRaw(diwassStore.credentials))
    deletingCredentials.value = false
  }

  async function setup() {
    await diwassStore.fetchCredentials(customer.id)

    if (!diwassStore.credentials) {
      diwassStore.credentials = {
        id: undefined,
        username: '',
        webServiceClientId: '',
        authenticationKey: '',
      }
    }

    originalCredentials.value = structuredClone(toRaw(diwassStore.credentials))
  }

  onMounted(setup)

  onUnmounted(() => {
    diwassStore.credentials = null
  })
</script>

<template>
  <ConfirmationDialog
    v-model:show="deletingCredentials"
    :confirm="deleteCredentials"
    title="Weet je het zeker?"
    body="Deze gegevens zijn niet meer terug te halen in het LZP systeem."
  />

  <template v-if="diwassStore.credentials">
    <div class="input-group">
      <FormInput
        label="Username"
        v-model="diwassStore.credentials.username"
        placeholder="Voer uw gebruikersnaam in"
        :error="visibleErrors.username"
      />
      <FormInput
        label="Web Service Client ID"
        v-model="diwassStore.credentials.webServiceClientId"
        placeholder="Voer uw web service client id in"
        :error="visibleErrors.webServiceClientId"
      />

      <FormInput
        :class="{ 'authentication-key': diwassStore.credentials.id != null }"
        label="Authentication Key"
        v-model="diwassStore.credentials.authenticationKey"
        :placeholder="
          diwassStore.credentials.id == null ? 'Voer uw authenticatie sleutel in' : '*'.repeat(50)
        "
        :error="visibleErrors.authenticationKey"
      />
    </div>

    <div class="button-group">
      <UiButton
        label="Reset"
        v-if="diwassStore.credentials?.id != null"
        variant="negative"
        :disabled="submitting"
        @click="deletingCredentials = true"
        class="submit-button"
      />
      <UiButton
        label="Opslaan"
        variant="prominent"
        :disabled="submitting || !modified"
        @click="submit"
        class="submit-button"
      />
    </div>
  </template>
</template>

<style lang="css" scoped>
  .input-group {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
  }

  .authentication-key :deep(input::placeholder) {
    color: var(--colours-grey-5);
  }

  .submit-button {
    align-self: end;
  }

  .button-group {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: end;
    gap: var(--spacing-s);
  }
</style>
